import { put, race, take, call, select, delay } from 'redux-saga/effects'
import axios from '../../axios-local-api';
import * as actions from '../actions';
import { STOP_RSC_POLLING } from '../actions/actionTypes';

const parseSpeedCheckResults = res => {
  const { speedTestResults = [] } = (res && res.data) || {};

  return speedTestResults.map(result => ({
    timestamp: new Date(result.timestamp).getTime(),
    up: result.uploadSpeed,
    down: result.downloadSpeed,
    serverName: result.serverName || 'N/A',
    serverId: result.serverId || 'unknown',
    testType: result.testType || 'N/A',
    sourceSystem: result.sourceSystem || 'N/A',
    subscribedUpSpeed: result.subscribedUpload || null,
    subscribedDownSpeed: result.subscribedDownload || null,
    pass: result.pass ?? 'N/A'
  }));
}

const startTimeFromReduxStore = (state) => state.speedCheck.speedCheckStartDate

const checkForTimeOut = (startTime, timeoutValue) => {
  const t = timeoutValue === 'true' ? 1000 : 90000
  if (new Date().getTime() - startTime >= t) {
    throw new Error('TIME OUT ON POLLING')
  }
}

export function* startRouterSpeedCheckSaga() {
  const routerMac = yield sessionStorage.getItem('mac');
  const sessionToken = yield sessionStorage.getItem('sessionToken');
  const techMobile = yield sessionStorage.getItem('techMobile');
  const browser = yield techMobile === 'true' ? "TECH_MOBILE" : "WEB_CLIENT"
  const timeoutValue = yield sessionStorage.getItem('enableSpeedCheckTimeoutTestingValue')
  const t = timeoutValue === 'true' ? 0 : 30000;
  if (sessionToken) {
    try {
      // 1. Submit `Start Speed Test` request (swagger /speedTest/start -> toStartTest).
      let { data } = yield axios.post('/speedTest/start', null, { params: { routerMac } });

      // 2. Resolve the speedTestId from the start response. The /speedTest/start
      //    (toStartTest) response returns it as `requestId`; fall back to other
      //    known keys defensively. Bail out early if it's missing so we don't
      //    poll /speedTest/fetchResult with an undefined id until the timeout.
      const speedTestId = data?.requestId;
      if (!speedTestId) {
        yield put(actions.logSpeedCheckResults(routerMac, browser, new Date().getTime(), `WITH ERROR: No speedTestId in start response`))
        yield put(actions.setRouterSpeedResultsFail(new Error('No speedTestId returned from /speedTest/start')));
        return;
      }

      // 3.0 Store the start time (drives the spinner).
      yield put(actions.setSpeedCheckStartDate(new Date(data.createdAt).getTime()));

      // 3.1 Wait before the first poll (average response time is ~25s)
      yield delay(t)

      // 3.2 Start polling (speedTestId passed straight to the worker)
      yield call(resultsPollingWatcher, speedTestId)

    } catch (startSpeedCheckError) {
      yield put(actions.logSpeedCheckResults(routerMac, browser, new Date().getTime(), `WITH ERROR: ${startSpeedCheckError}`))
      yield put(actions.setRouterSpeedResultsFail(startSpeedCheckError));
    }
  } else {
    console.error('NO TOKEN FOUND')
  }
}

export function* fetchRouterSpeedCheckResultsSaga() {
  const routerMac = yield sessionStorage.getItem('mac');
  const sessionToken = yield sessionStorage.getItem('sessionToken');

  if (sessionToken) {
    try {
      let rscResultsRes = yield axios.get('/speedTest/results', { params: { routerMac } })
      yield put(actions.setResultsLoaded(false));
      yield put(actions.setResultsLoading(true));
      let parsedRouterSpeedCheckResults = yield parseSpeedCheckResults(rscResultsRes)
      yield put(actions.setSpeedCheckResults(parsedRouterSpeedCheckResults));
      yield put(actions.setResultsLoaded(true));
      yield put(actions.setResultsLoading(false));
      // Refresh the Change Log whenever speed test results are fetched.
      yield put(actions.initLogs());
    } catch (speedCheckResultsError) {
      yield put(actions.setRouterSpeedResultsFail(speedCheckResultsError))
      // yield put(actions.setResultsLoading(false));
    }
  } else {
    yield put(actions.setResultsLoaded(true));
    yield put(actions.setResultsLoading(false));
    //console.log('NO TOKEN')
  }
}

// export function* checkRouterSpeedCheckVisibilitySaga() {
//   const routerMac = yield sessionStorage.getItem('mac');
//   const sessionToken = yield sessionStorage.getItem('sessionToken');
//   if (sessionToken) {
//     try {
//       let response = yield axios.put('speedTest/enable',
//         {
//           'enable': true,
//           'enableAllNodes': true
//         },
//         {
//           params: { routerMac }
//         });
//       yield put(actions.setRouterSpeedVisiblity(response && response.status === 200))
//     } catch (speedCheckNotAvailableError) {
//       yield put(actions.setRouterSpeedResultsFail(speedCheckNotAvailableError));
//     }
//   } else {
//     console.log('NO TOKEN')
//   }
// }

export function* resultsPollingWatcher(speedTestId) {
  yield race({
    task: call(resultsPollingWorker, speedTestId),
    cancel: take(STOP_RSC_POLLING)
  })
}

const POLL_DELAY = 30000

// While the test is still running /fetchResult returns speedResult.status === 'Queued'.
// Any other status (e.g. 'failed') or an explicit success flag is terminal.
const isSpeedTestComplete = (data) => {
  const speedResult = data && data.speedResult;
  if (!speedResult) return false;
  const status = (speedResult.status || '').toLowerCase();
  if (status === 'queued') return false;
  return typeof speedResult.success === 'boolean' || !!status;
}

export function* resultsPollingWorker(speedTestId) {
  // Guard: never poll /speedTest/fetchResult without a valid id.
  if (!speedTestId) {
    yield put(actions.setRouterSpeedResultsFail(new Error('Missing speedTestId for polling')));
    return;
  }

  let routerMac;
  let browser;
  try {
    routerMac = yield sessionStorage.getItem('mac');
    const techMobile = yield sessionStorage.getItem('techMobile');
    browser = yield techMobile === 'true' ? "TECH_MOBILE" : "WEB_CLIENT"
  } catch (e) {
    browser = "WEB_CLIENT"
  }

  while (1) {
    try {
      // Fetch the start time from the store (speedTestId is passed in as an arg)
      const speedTestStartedAt = yield select(startTimeFromReduxStore)
      const timeoutValue = yield sessionStorage.getItem('enableSpeedCheckTimeoutTestingValue')

      // Check for timeout
      try {
        yield checkForTimeOut(speedTestStartedAt, timeoutValue)
      } catch (timeOutError) {
        yield put(actions.logSpeedCheckResults(routerMac, browser, (new Date().getTime() - speedTestStartedAt), `FAILED`))
        yield put(actions.setRouterSpeedResultsFail(timeOutError))
        yield put({ type: STOP_RSC_POLLING })
        return
      }

      // Poll /speedTest/fetchResult with the speedTestId (swagger -> toFetchResult)
      try {
        const pollRes = yield axios.get('/speedTest/fetchResult', { params: { speedTestId } })

        if (isSpeedTestComplete(pollRes.data)) {
          // Test finished -> stop spinner, refresh history + latest up/down.
          yield put(actions.logSpeedCheckResults(routerMac, browser, (new Date().getTime() - speedTestStartedAt), `SUCCESS`))
          yield put(actions.setSpeedCheckStartDate(null))

          try {
            const historyRes = yield axios.get('/speedTest/results', { params: { routerMac } })
            const parsedResults = yield parseSpeedCheckResults(historyRes)
            yield put(actions.setSpeedCheckResults(parsedResults))
            // Refresh the Change Log whenever speed test results are fetched.
            yield put(actions.initLogs());
          } catch (historyError) {
            yield put(actions.setRouterSpeedResultsFail(historyError))
          }

          yield put({ type: STOP_RSC_POLLING })
          return
        }

        // Still 'Queued' -> wait and poll again.
        yield delay(POLL_DELAY)
      } catch (errorWhilePolling) {
        // 404 = RTCCA has no event yet (still running) -> keep polling.
        if (errorWhilePolling.response && errorWhilePolling.response.status === 404) {
          yield delay(POLL_DELAY)
        } else {
          yield put(actions.logSpeedCheckResults(routerMac, browser, (new Date().getTime() - speedTestStartedAt), `WITH ERROR: ${errorWhilePolling}`))
          yield put(actions.setRouterSpeedResultsFail(errorWhilePolling))
          yield put({ type: STOP_RSC_POLLING })
          return
        }
      }
    } catch (fetchingLocalStateError) {
      yield put(actions.logSpeedCheckResults(routerMac, browser, undefined, `WITH ERROR: ${fetchingLocalStateError}`))
      yield put(actions.setRouterSpeedResultsFail(fetchingLocalStateError))
      yield put({ type: STOP_RSC_POLLING })
      return
    }
  }
}

export function* logSpeedCheckSaga(action) {
  // REQUIREMENTS FROM WIN-309
  // status -> "SUCCESSFUL", "FAILED", "WITH ERROR"
  // "SUCCESSFUL": every thing went fine
  // "FAILED": NO RESPOSNE containing RESULTS (e.g. timeout)
  // "WITH ERROR": this case occurs when the initial call to START speed test fails (INCLUDE ERROR DATA)

  const { routerMac, browser, elapsedTime, status } = yield action

  const rscLoggingEndpoint = yield {
    method: 'post',
    url: '/speedTest/logSpeedTestEvent',
    data: {
      routerMac,
      browser,
      duration: elapsedTime,
      status,
    }
  }

  try {
    yield call(axios, rscLoggingEndpoint);
  } catch (e) {
    yield console.error('Error while attempting to log Speed Check event', e);
  }
}